import {useEffect, useRef, useState, useCallback} from "react"

/* A custom hook that runs a function once when the component mounts. Since this is built on
 * useEffect, the function can return a function to act as an "onUnmount" handler if necessary.
 */
export function useOnMount(fn) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lack of deps is intentional
  useEffect(fn, [])
}

/* A custom hook that runs an effect when dependencies change, but *not* on initial mount. The `fn`
 * will receive the *previous* values of the deps as arguments. Use as a replacement for
 * componentDidUpdate.
 */
export function useOnUpdate(fn, deps) {
  if (!deps || (Array.isArray(deps) && deps.length === 0)) {
    throw new Error("useOnUpdate requires deps")
  }

  const prevDeps = useRef(null)
  useEffect(() => {
    if (prevDeps.current !== null) {
      fn(...prevDeps.current)
    }
    prevDeps.current = deps
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are passed through, don't need to be literal here
  }, deps)
}

/* A custom hook that behaves like useState, except that the setter returns a promise which resolves
 * to the final value of the state *after* the update completes (when the component re-renders).
 * Useful for serializing actions & ensuring responses to state changes trigger after the change
 * is fully applied. */
export function usePState(init) {
  const
    [val, setVal] = useState(init),
    pendingResolves = useRef([]),
    setter = useCallback(update => {
      const {promise, resolve} = Promise.withResolvers()
      pendingResolves.current.push(resolve)
      setVal(update)
      return promise
    }, [])

  useEffect(() => {
    pendingResolves.current.forEach(r => r(val))
    pendingResolves.current = []
  }, [val])

  return [val, setter]
}
